import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./FlurryPaywall.module.scss"

/**
 * FlurryPaywall (`/flurry`) — an original, self-contained NON-ONBOARDING paywall
 * for Private Photo Vault Premium. Paywall type rolled at random for this run:
 * NON-ONBOARDING (a mid-app upsell — not first launch, not expiration, not a
 * single feature gate).
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * Your memories are a whole little WORLD held inside a glass keepsake snow-globe
 * on a sunlit sill. In the free vault that world sits out in the open — a single
 * copy, unsealed. You give the globe a SHAKE; a warm flurry of light lifts and
 * settles, and as it settles the dome SEALS onto its walnut base: a brass plaque
 * engraves "SEALED · PRIVATE" and five real Premium guarantees light up one by
 * one. The resting state we sell is the sealed, glowing globe — your world, kept.
 *
 * ── Style directive (read live from #paywalls each run) ─────────────────────
 * The newest @Paywalls Bot message this run was exactly `default` — the explicit
 * reset keyword — so NO standing style directive is active. This is a free
 * concept / full-divergence run: aesthetic and concept chosen autonomously.
 *
 * ── Divergence (see claude-paywalls/_ledger.md — 66 prior concepts) ─────────
 * Layout      : a single GLASS SNOW-GLOBE object on a walnut base on a warm sill
 *               — a self-contained little world under a dome. Not hero+card,
 *               constellation, calendar, arch, receipt, chat, map, book, media
 *               player, control console, gravity scene or vital-sign monitor.
 * Interaction : SHAKE-TO-SETTLE — stir the globe (tap / press / drag) and a
 *               flurry lifts and falls; the settle seals the dome and lights the
 *               guards. Distinct from draw, hold, rotary/dial, drag-divider,
 *               haul, flip, stamp, zip, master-switch, rhythmic-tap, pour,
 *               peel-and-stick, fold. Nothing in the ledger shakes.
 * Register    : cozy, wondrous, quietly magical — a keepsake on the windowsill.
 *               A deliberate step away from the recent bright-gleeful (Sticker
 *               Album) and the earlier fear streak alike: warm, calm delight.
 * Art dir     : bright warm sunlit glass — cream / apricot daylight, honey-gold
 *               flurry, real photographic prints inside a caustic glass dome.
 * Value frame : SELF-CONTAINED WHOLENESS + ENDOWMENT — "everything you love,
 *               already here; Premium seals the whole little world and keeps it."
 * Hero motif  : a snow-globe of your own memories that you shake, and it seals.
 *
 * ── Honest & App Store compliant ───────────────────────────────────────────
 * All five things that light up are REAL Premium features (Cloud Vault encrypted
 * second copy, full-quality originals, sync across devices, Break-In Report,
 * 30-day Trash Recovery). Sealing only turns protection ON — explicit copy that
 * photos never leave the vault and that nothing is moved or deleted, ever. No
 * countdown, no fake scarcity, no data-loss-on-expiration threat. Savings are
 * real arithmetic from live product data. Yearly is the featured, default, most
 * prominent price; monthly is small and secondary. Interior images are labelled
 * Unsplash demo photos, not the user's own library.
 */

const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v))

// small seeded PRNG so the flurry's resting arrangement is stable across renders
function mulberry32(a: number) {
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

// honey-gold motes suspended in the globe — a uniform-ish disk of drifting light
const rnd = mulberry32(0x20240703)
const MOTES = Array.from({ length: 34 }, () => {
  const ang = rnd() * Math.PI * 2
  const rad = Math.sqrt(rnd()) * 45 // sqrt → even fill across the disk
  return {
    x: 50 + Math.cos(ang) * rad,
    y: 45 + Math.sin(ang) * rad * 0.92,
    s: 1.4 + rnd() * 3.6,
    d: rnd() * 3.2,
    dur: 2.8 + rnd() * 3.4,
    o: 0.32 + rnd() * 0.5,
  }
})

// the little world inside the dome — a cluster of the user's own memories.
// Reuses bundled Unsplash demo photos already in the repo (no external egress).
type Card = {
  src: string
  x: number
  y: number
  r: number
  w: number
  z: number
  video?: boolean
}
const CARDS: Card[] = [
  { src: "/recall/flower-1.jpg", x: 33, y: 60, r: -15, w: 50, z: 1 },
  { src: "/recall/city-1.jpg", x: 67, y: 62, r: 15, w: 50, z: 1 },
  { src: "/recall/coffee-1.jpg", x: 30, y: 41, r: -11, w: 58, z: 2 },
  { src: "/recall/beach-1.jpg", x: 69, y: 43, r: 12, w: 58, z: 2 },
  { src: "/recall/sunset-1.jpg", x: 41, y: 55, r: -5, w: 60, z: 3 },
  { src: "/recall/mountain-1.jpg", x: 60, y: 55, r: 7, w: 58, z: 3 },
  { src: "/recall/dog-1.jpg", x: 50, y: 34, r: 3, w: 64, z: 4, video: true },
]

// the five real Premium guarantees — each lights as the flurry settles
const GUARDS = [
  {
    key: "backup",
    Icon: svg.featureIcons.CloudVault,
    name: "Cloud Vault",
    line: "a private, encrypted second copy",
  },
  {
    key: "originals",
    Icon: svg.featureIcons.Star,
    name: "Full-quality originals",
    line: "kept exactly as you shot them",
  },
  {
    key: "devices",
    Icon: svg.featureIcons.Wifi,
    name: "On every device",
    line: "your world, synced iPhone to Mac",
  },
  {
    key: "breakin",
    Icon: svg.featureIcons.Breakin,
    name: "Break-In Report",
    line: "photo + location on a wrong PIN",
  },
  {
    key: "trash",
    Icon: svg.featureIcons.Trash,
    name: "Trash Recovery",
    line: "a 30-day safety net for deletes",
  },
]

type PlanKey = "yearly" | "monthly"

const FlurryPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$3.00"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = premium?.hasTrial ?? yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    const p = Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
    return p > 0 ? p : 0
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // lit = how many guarantees have settled (0..5); sealed once all five are lit
  const [lit, setLit] = useState(0)
  const [shaking, setShaking] = useState(false)
  const sealed = lit >= GUARDS.length

  const litTimers = useRef<number[]>([])
  const shakeTimer = useRef<number>(0)
  const userActed = useRef(false)

  const clearLit = useCallback(() => {
    litTimers.current.forEach((t) => clearTimeout(t))
    litTimers.current = []
  }, [])

  // one shake: the flurry lifts, then the guarantees settle in one by one and
  // the dome seals. Re-shaking simply replays the flurry.
  const shake = useCallback(() => {
    clearLit()
    clearTimeout(shakeTimer.current)
    setShaking(true)
    shakeTimer.current = window.setTimeout(() => setShaking(false), 900)
    setLit(0)
    for (let i = 1; i <= GUARDS.length; i++) {
      litTimers.current.push(
        window.setTimeout(
          () => setLit((n) => clamp(Math.max(n, i), 0, GUARDS.length)),
          360 + i * 300,
        ),
      )
    }
  }, [clearLit])

  const onShakeClick = useCallback(() => {
    userActed.current = true
    shake()
  }, [shake])

  // auto-teach on mount: settle to the sealed, glowing resting state we sell.
  // A real interaction from the user cancels the pending auto-run.
  useEffect(() => {
    if (reduceMotion) {
      setLit(GUARDS.length)
      return
    }
    const start = window.setTimeout(() => {
      if (!userActed.current) shake()
    }, 650)
    return () => {
      clearTimeout(start)
      clearTimeout(shakeTimer.current)
      clearLit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion])

  // dragging across the globe also stirs the flurry (once, per gesture)
  const dragged = useRef(false)
  const onPointerDown = useCallback(() => {
    dragged.current = false
  }, [])
  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (e.buttons === 0 || dragged.current) return
      dragged.current = true
      userActed.current = true
      shake()
    },
    [shake],
  )

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  return (
    <div
      className={`${styles.root} ${sealed ? styles.sealed : ""} ${
        shaking ? styles.shaking : ""
      }`}
    >
      <div className={styles.sun} aria-hidden="true" />

      {/* header — brand clarity in the first glance */}
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M12 2.6l6.8 2.6v5.3c0 4.4-2.9 8-6.8 9.4-3.9-1.4-6.8-5-6.8-9.4V5.2L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9 12.2l2.2 2.2L15 10.4"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandName}>
            Private Photo Vault
            <span className={styles.brandTag}>Premium</span>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={() => native.postNativeEvent("close")}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15">
            <path
              d="M6 6l12 12M18 6L6 18"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* intro */}
      <section className={styles.intro}>
        <h1 className={styles.title}>
          {sealed ? (
            <>
              Your whole little world,
              <br />
              <span className={styles.titleAccent}>sealed under glass.</span>
            </>
          ) : (
            <>
              A whole little world
              <br />
              <span className={styles.titleAccent}>
                of everything you love.
              </span>
            </>
          )}
        </h1>
        <p className={styles.sub}>
          {sealed
            ? "Kept as one — a private encrypted copy, full-quality originals, on every device you own."
            : "Every photo and video you keep lives in here. Give it a shake to seal the whole world safely under glass."}
        </p>
      </section>

      {/* ── the keepsake globe ──────────────────────────────────────────── */}
      <section className={styles.stage}>
        <div className={styles.globeCol}>
          <button
            type="button"
            className={styles.globe}
            onClick={onShakeClick}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            aria-label={
              sealed ? "Shake the globe again" : "Shake the globe to seal it"
            }
          >
            <span className={styles.glow} aria-hidden="true" />
            <span className={styles.world} aria-hidden="true">
              <span className={styles.drift} />
              {CARDS.map((c) => (
                <span
                  key={c.src}
                  className={styles.card}
                  style={{
                    left: `${c.x}%`,
                    top: `${c.y}%`,
                    width: `${c.w}px`,
                    ["--r" as any]: `${c.r}deg`,
                    zIndex: c.z,
                  }}
                >
                  <img src={c.src} alt="" draggable={false} />
                  {c.video && (
                    <span className={styles.play}>
                      <svg viewBox="0 0 24 24" width="13" height="13">
                        <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
                      </svg>
                    </span>
                  )}
                </span>
              ))}
              <span className={styles.motes}>
                {MOTES.map((m, i) => (
                  <span
                    key={i}
                    className={styles.mote}
                    style={{
                      left: `${m.x}%`,
                      top: `${m.y}%`,
                      width: `${m.s}px`,
                      height: `${m.s}px`,
                      opacity: m.o,
                      ["--d" as any]: `${m.d}s`,
                      ["--dur" as any]: `${m.dur}s`,
                    }}
                  />
                ))}
              </span>
            </span>
            {/* glass caustics on top of the world */}
            <span className={styles.glassShine} aria-hidden="true" />
            <span className={styles.glassStreak} aria-hidden="true" />
            <span className={styles.glassRim} aria-hidden="true" />
          </button>

          {/* walnut base + brass seal plaque */}
          <div className={styles.base}>
            <span className={styles.neck} aria-hidden="true" />
            <span className={styles.plaque}>
              <span className={styles.plaqueLock} aria-hidden="true">
                <svg viewBox="0 0 24 24" width="11" height="11">
                  <rect
                    x="5"
                    y="10.5"
                    width="14"
                    height="9.5"
                    rx="2"
                    fill="currentColor"
                  />
                  <path
                    d="M8 10.5V8a4 4 0 0 1 8 0v2.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.9"
                  />
                </svg>
              </span>
              {sealed ? "SEALED · PRIVATE" : "SHAKE TO SEAL"}
            </span>
          </div>
          <div className={styles.sill} aria-hidden="true" />
        </div>

        <div className={styles.stageFoot}>
          <button
            type="button"
            className={styles.shakeBtn}
            onClick={onShakeClick}
          >
            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
              <path
                d="M12 3v3M12 18v3M4.5 7.5l2.1 2.1M17.4 14.4l2.1 2.1M3 12h3M18 12h3M4.5 16.5l2.1-2.1M17.4 9.6l2.1-2.1"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
            </svg>
            {sealed ? "Shake it again" : "Give it a shake"}
          </button>
          <span className={styles.progress} aria-live="polite">
            {sealed ? "Sealed · 5 guarantees on" : `${lit}/5 settling`}
          </span>
        </div>
      </section>

      {/* ── the five real guarantees, lighting as the flurry settles ─────── */}
      <ul className={styles.guards}>
        {GUARDS.map((g, i) => {
          const on = lit > i
          const Icon = g.Icon
          return (
            <li
              key={g.key}
              className={`${styles.guard} ${on ? styles.guardOn : ""}`}
              style={{ ["--i" as any]: i }}
            >
              <span className={styles.guardIcon} aria-hidden="true">
                <Icon />
              </span>
              <span className={styles.guardText}>
                <span className={styles.guardName}>{g.name}</span>
                <span className={styles.guardLine}>{g.line}</span>
              </span>
              <span className={styles.guardState} aria-hidden="true">
                {on ? (
                  <svg viewBox="0 0 24 24" width="15" height="15">
                    <path
                      d="M5 12.6l4.3 4.3L19 7.4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <span className={styles.guardOff} />
                )}
              </span>
            </li>
          )
        })}
      </ul>

      {/* honest, compliant reassurance */}
      <p className={styles.assure}>
        <span className={styles.assureMark} aria-hidden="true">
          <svg viewBox="0 0 24 24" width="13" height="13">
            <path
              d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        Sealing only turns protection on. Your photos never leave your vault —
        nothing is moved or deleted.
      </p>

      {/* ── PRICING: yearly featured, monthly small & secondary ──────────── */}
      <div className={styles.plans}>
        <button
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>Sealed · Yearly</span>
            {savingsPct > 0 && (
              <span className={styles.saveTag}>Save {savingsPct}%</span>
            )}
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 12.5l4.2 4.2L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPerMo}>
            about {yearlyPerMonth}/mo, billed once a year
          </span>
        </button>

        <button
          className={`${styles.monthly} ${plan === "monthly" ? styles.monthlySel : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <p className={styles.credit}>Demo photos from Unsplash</p>

      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly"
              ? hasTrial
                ? "Seal your world — free for 7 days"
                : "Seal your world"
              : "Seal it monthly"}
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? hasTrial
                ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
                : `${yearlyPrice}/yr · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
        </button>
      </div>
    </div>
  )
}

export default FlurryPaywall
